import TabView from '@/components/TabView/TabView';
import styles from './Contacts.module.css';
import { usePeopleContacts, useOrganizationContacts } from './hooks';
import {
  Table,
  LoadingOverlay,
  Stack,
  Text,
  Group,
  Pagination,
  Center,
  Box,
  Select,
  TextInput,
  Button,
  Flex,
  ActionIcon,
} from '@mantine/core';
import {
  UserIcon,
  Building2Icon,
  SearchIcon,
  SortAscIcon,
  SortDescIcon,
  XIcon,
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useMemo, useCallback } from 'react';
import {
  HumanData,
  HumanDocument,
  OrganizationData,
  OrganizationDocument,
} from '../../database/baseTypes';
import { WhereClause, OrderByClause } from '../../database/queryBuilder';
import { LoadingView } from '@/components/LoadingView';
import ClientDetailDrawer from '@/components/Drawers/ClientDetailDrawer/ClientDetailDrawer';

/**
 * WARNING: This component was generated by Claude and is not yet fully tested.
 */
const Contacts = () => {
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();

  const tabs = [
    {
      id: 'people',
      label: <span>People</span>,
      content: <PeopleTable />,
    },
    {
      id: 'organizations',
      label: <span>Organizations</span>,
      content: <OrganizationsTable />,
    },
  ];

  return (
    <div className={styles.contactsRoot}>
      <TabView
        tabs={tabs}
        activeTabId={tab ?? 'people'}
        onActiveTabChange={(id) => navigate(`/contacts/${id}`)}
        contentClassName={styles.scrollableContent}
      />
    </div>
  );
};

export default Contacts;

type FilterField<T> = {
  field: keyof T;
  value: string;
  operator: '==' | '>' | '<' | '>=' | '<=' | 'array-contains' | 'in';
};

const PeopleTable = () => {
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [filters, setFilters] = useState<FilterField<HumanData>[]>([]);
  const [sortField, setSortField] = useState<keyof HumanData>('lastName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterField, setFilterField] = useState<keyof HumanData>('lastName');
  const [filterValue, setFilterValue] = useState('');
  // `selectedHuman` is intentionally kept around even after the drawer
  // closes so its content stays mounted through Mantine's slide-out
  // transition. Clearing it on close would render a blank drawer for the
  // duration of the animation. The next selection replaces it.
  const [selectedHuman, setSelectedHuman] = useState<HumanDocument>();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Convert filters to Firestore WhereClause
  const firestoreFilters = useMemo<WhereClause<HumanData>[]>(() => {
    return filters.map((filter) => [
      filter.field,
      filter.operator,
      filter.value,
    ]);
  }, [filters]);

  // Convert sort to Firestore OrderByClause
  const firestoreSort = useMemo<OrderByClause<HumanData>[]>(() => {
    return [[sortField, sortDirection]];
  }, [sortField, sortDirection]);

  const { humansWithMatters, isLoading, pagination } = usePeopleContacts(
    page,
    pageSize,
    firestoreFilters,
    firestoreSort,
  );

  const addFilter = useCallback(() => {
    if (!filterValue.trim()) return;

    setFilters((prev) => [
      ...prev,
      { field: filterField, value: filterValue, operator: '==' },
    ]);
    setFilterValue('');
    setPage(1); // Reset to first page when filter changes
  }, [filterField, filterValue]);

  const removeFilter = useCallback((index: number) => {
    setFilters((prev) => prev.filter((_, i) => i !== index));
    setPage(1); // Reset to first page when filter changes
  }, []);

  const toggleSortDirection = useCallback(() => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    setPage(1); // Reset to first page when sort changes
  }, []);

  const showFullLoading = isLoading && humansWithMatters.length === 0;

  if (showFullLoading) {
    return <LoadingView includeAnimation includeTitle={false} />;
  }

  return (
    <Stack>
      {/* Filtering and Sorting Controls */}
      <Box
        bg='gray.0'
        p='md'
        style={{ border: '1px solid var(--mantine-color-gray-2)' }}
      >
        <Stack gap='md'>
          <Flex gap='md' align='flex-end'>
            <Select
              label='Filter by'
              data={[
                { value: 'firstName', label: 'First Name' },
                { value: 'lastName', label: 'Last Name' },
              ]}
              value={filterField}
              onChange={(value) =>
                value && setFilterField(value as keyof HumanData)
              }
              style={{ flex: 1 }}
            />
            <TextInput
              label='Filter value'
              value={filterValue}
              onChange={(e) => setFilterValue(e.currentTarget.value)}
              placeholder='Enter filter value'
              style={{ flex: 2 }}
              rightSection={
                <ActionIcon onClick={addFilter} disabled={!filterValue.trim()}>
                  <SearchIcon size={16} />
                </ActionIcon>
              }
            />
            <Button onClick={addFilter} disabled={!filterValue.trim()}>
              Add Filter
            </Button>
          </Flex>

          {/* Active Filters */}
          {filters.length > 0 && (
            <Flex gap='sm' wrap='wrap'>
              {filters.map((filter, index) => (
                <Button
                  key={index}
                  variant='light'
                  size='xs'
                  color='gray'
                  rightSection={
                    <ActionIcon size='xs' onClick={() => removeFilter(index)}>
                      <XIcon size={14} />
                    </ActionIcon>
                  }
                >
                  {String(filter.field)} {filter.operator} {filter.value}
                </Button>
              ))}
            </Flex>
          )}

          {/* Sort Controls */}
          <Flex gap='md' align='flex-end'>
            <Select
              label='Sort by'
              data={[
                { value: 'firstName', label: 'First Name' },
                { value: 'lastName', label: 'Last Name' },
              ]}
              value={sortField}
              onChange={(value) =>
                value && setSortField(value as keyof HumanData)
              }
              style={{ flex: 1 }}
            />
            <ActionIcon onClick={toggleSortDirection} variant='light' size='lg'>
              {sortDirection === 'asc' ? (
                <SortAscIcon size={16} />
              ) : (
                <SortDescIcon size={16} />
              )}
            </ActionIcon>
          </Flex>
        </Stack>
      </Box>

      <Box pos='relative'>
        <LoadingOverlay
          visible={isLoading}
          overlayProps={{ radius: 'sm', blur: 1 }}
        />
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Phone</Table.Th>
              <Table.Th>Matters</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {humansWithMatters.length > 0 ? (
              humansWithMatters.map((human) => (
                <Table.Tr
                  key={human.id}
                  tabIndex={0}
                  role='button'
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedHuman(human);
                    setDrawerOpen(true);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedHuman(human);
                      setDrawerOpen(true);
                    }
                  }}
                >
                  <Table.Td>
                    <Group gap='sm'>
                      <UserIcon size={16} />
                      <Text>
                        {human.firstName} {human.lastName}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>{human.emailAddresses?.[0]?.value}</Table.Td>
                  <Table.Td>{human.phoneNumbers?.[0]?.value}</Table.Td>
                  <Table.Td>{human.matters?.length || 0}</Table.Td>
                </Table.Tr>
              ))
            ) : (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text ta='center' fs='italic' c='dimmed'>
                    No contacts found
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Box>

      {pagination.totalPages > 1 && (
        <Center mt='md'>
          <Pagination
            total={pagination.totalPages}
            value={page}
            onChange={setPage}
            disabled={isLoading}
          />
        </Center>
      )}

      <ClientDetailDrawer
        contact={
          selectedHuman ? { type: 'human', document: selectedHuman } : undefined
        }
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </Stack>
  );
};

const OrganizationsTable = () => {
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [filters, setFilters] = useState<FilterField<OrganizationData>[]>([]);
  const [sortField, setSortField] =
    useState<keyof OrganizationData>('companyName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterField, setFilterField] =
    useState<keyof OrganizationData>('companyName');
  const [filterValue, setFilterValue] = useState('');
  // See PeopleTable — same rationale for splitting the drawer-open state
  // from the selected-organization state.
  const [selectedOrganization, setSelectedOrganization] =
    useState<OrganizationDocument>();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Convert filters to Firestore WhereClause
  const firestoreFilters = useMemo<WhereClause<OrganizationData>[]>(() => {
    return filters.map((filter) => [
      filter.field,
      filter.operator,
      filter.value,
    ]);
  }, [filters]);

  // Convert sort to Firestore OrderByClause
  const firestoreSort = useMemo<OrderByClause<OrganizationData>[]>(() => {
    return [[sortField, sortDirection]];
  }, [sortField, sortDirection]);

  const { organizationsWithMatters, isLoading, pagination } =
    useOrganizationContacts(page, pageSize, firestoreFilters, firestoreSort);

  const addFilter = useCallback(() => {
    if (!filterValue.trim()) return;

    setFilters((prev) => [
      ...prev,
      { field: filterField, value: filterValue, operator: '==' },
    ]);
    setFilterValue('');
    setPage(1); // Reset to first page when filter changes
  }, [filterField, filterValue]);

  const removeFilter = useCallback((index: number) => {
    setFilters((prev) => prev.filter((_, i) => i !== index));
    setPage(1); // Reset to first page when filter changes
  }, []);

  const toggleSortDirection = useCallback(() => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    setPage(1); // Reset to first page when sort changes
  }, []);

  const showFullLoading = isLoading && organizationsWithMatters.length === 0;

  if (showFullLoading) {
    return <LoadingView includeAnimation includeTitle={false} />;
  }

  return (
    <Stack>
      {/* Filtering and Sorting Controls */}
      <Box
        bg='gray.0'
        p='md'
        style={{ border: '1px solid var(--mantine-color-gray-2)' }}
      >
        <Stack gap='md'>
          <Flex gap='md' align='flex-end'>
            <Select
              label='Filter by'
              data={[{ value: 'companyName', label: 'Company Name' }]}
              value={filterField}
              onChange={(value) =>
                value && setFilterField(value as keyof OrganizationData)
              }
              style={{ flex: 1 }}
            />
            <TextInput
              label='Filter value'
              value={filterValue}
              onChange={(e) => setFilterValue(e.currentTarget.value)}
              placeholder='Enter filter value'
              style={{ flex: 2 }}
              rightSection={
                <ActionIcon onClick={addFilter} disabled={!filterValue.trim()}>
                  <SearchIcon size={16} />
                </ActionIcon>
              }
            />
            <Button onClick={addFilter} disabled={!filterValue.trim()}>
              Add Filter
            </Button>
          </Flex>

          {/* Active Filters */}
          {filters.length > 0 && (
            <Flex gap='sm' wrap='wrap'>
              {filters.map((filter, index) => (
                <Button
                  key={index}
                  variant='light'
                  size='xs'
                  color='gray'
                  rightSection={
                    <ActionIcon size='xs' onClick={() => removeFilter(index)}>
                      <XIcon size={14} />
                    </ActionIcon>
                  }
                >
                  {String(filter.field)} {filter.operator} {filter.value}
                </Button>
              ))}
            </Flex>
          )}

          {/* Sort Controls */}
          <Flex gap='md' align='flex-end'>
            <Select
              label='Sort by'
              data={[{ value: 'companyName', label: 'Company Name' }]}
              value={sortField}
              onChange={(value) =>
                value && setSortField(value as keyof OrganizationData)
              }
              style={{ flex: 1 }}
            />
            <ActionIcon onClick={toggleSortDirection} variant='light' size='lg'>
              {sortDirection === 'asc' ? (
                <SortAscIcon size={16} />
              ) : (
                <SortDescIcon size={16} />
              )}
            </ActionIcon>
          </Flex>
        </Stack>
      </Box>

      <Box pos='relative'>
        <LoadingOverlay
          visible={isLoading}
          overlayProps={{ radius: 'sm', blur: 1 }}
        />
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Matters</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {organizationsWithMatters.length > 0 ? (
              organizationsWithMatters.map((org) => (
                <Table.Tr
                  key={org.id}
                  tabIndex={0}
                  role='button'
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedOrganization(org);
                    setDrawerOpen(true);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedOrganization(org);
                      setDrawerOpen(true);
                    }
                  }}
                >
                  <Table.Td>
                    <Group gap='sm'>
                      <Building2Icon size={16} />
                      <Text>{org.companyName}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>{org.matters?.length || 0}</Table.Td>
                </Table.Tr>
              ))
            ) : (
              <Table.Tr>
                <Table.Td colSpan={2}>
                  <Text ta='center' fs='italic' c='dimmed'>
                    No organizations found
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Box>

      {pagination.totalPages > 1 && (
        <Center mt='md'>
          <Pagination
            total={pagination.totalPages}
            value={page}
            onChange={setPage}
            disabled={isLoading}
          />
        </Center>
      )}

      <ClientDetailDrawer
        contact={
          selectedOrganization
            ? { type: 'organization', document: selectedOrganization }
            : undefined
        }
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </Stack>
  );
};
